import React from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import type { CardDefinition, CardRarity } from "../api/respectCardsApi";
import RespectCard from "./RespectCard";
import { Button } from "../../../ui/components/Button";
import { RARITY_BURST_COLOR } from "../lib/cardState";
import "../styles/cardReveal.css";

export interface CardRevealOverlayProps {
  card: CardDefinition | null;
  open: boolean;
  /** Optional headline shown above the card. Defaults to "You pulled". */
  headline?: string;
  onClose: () => void;
}

type RevealStage = "burst" | "flipping" | "revealed";

const STAGE_DURATIONS: Record<RevealStage, number> = {
  burst: 650,
  flipping: 700,
  revealed: 0,
};

const RARITY_LABEL: Record<CardRarity, string> = {
  common: "Common",
  rare: "Rare",
  epic: "Epic",
  legendary: "Legendary",
};

/** Burst density per rarity; the colour comes from the shared token map. */
const RARITY_BURST_COUNT: Record<CardRarity, number> = {
  common: 8,
  rare: 12,
  epic: 18,
  legendary: 24,
};

function Sparkles({ rarity }: { rarity: CardRarity }) {
  const count = RARITY_BURST_COUNT[rarity] ?? RARITY_BURST_COUNT.common;
  const color = RARITY_BURST_COLOR[rarity] ?? RARITY_BURST_COLOR.common;
  const items = React.useMemo(
    () =>
      Array.from({ length: count }, (_, i) => {
        const angle = (i / count) * Math.PI * 2;
        const distance = 110 + Math.random() * 60;
        return {
          id: i,
          x: Math.cos(angle) * distance,
          y: Math.sin(angle) * distance,
          delay: Math.random() * 0.12,
          size: 6 + Math.random() * 8,
        };
      }),
    [count],
  );

  return (
    <div className="card-reveal__sparkles" aria-hidden>
      {items.map((s) => (
        <motion.span
          key={s.id}
          className="card-reveal__sparkle"
          style={{ background: color, width: s.size, height: s.size }}
          initial={{ opacity: 0, scale: 0, x: 0, y: 0 }}
          animate={{
            opacity: [0, 1, 0],
            scale: [0, 1, 0.4],
            x: s.x,
            y: s.y,
          }}
          transition={{ duration: 0.9, delay: s.delay, ease: "easeOut" }}
        />
      ))}
    </div>
  );
}

export default function CardRevealOverlay({
  card,
  open,
  headline,
  onClose,
}: CardRevealOverlayProps) {
  const [stage, setStage] = React.useState<RevealStage>("burst");
  const rarity: CardRarity = card?.rarity ?? "common";

  // Drive the stage machine when the overlay opens.
  React.useEffect(() => {
    if (!open || !card) return undefined;
    setStage("burst");
    const t1 = window.setTimeout(() => setStage("flipping"), STAGE_DURATIONS.burst);
    const t2 = window.setTimeout(
      () => setStage("revealed"),
      STAGE_DURATIONS.burst + STAGE_DURATIONS.flipping,
    );
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [open, card]);

  // Lock background scroll + close on Escape.
  React.useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!card) return null;

  const revealText = headline ?? "You pulled";
  const tcgFlipClass = `card-reveal__tcg card-reveal__tcg--${rarity} card-reveal__tcg--${stage}`;
  const revealed = stage === "revealed";

  // Hand-expiry contract, computed client-side to match the backend exactly
  // (now + N*24h). Omitted entirely when the card never auto-expires from hand.
  const validityDays = card.unplayedValidityDays;
  const handExpiryLine =
    typeof validityDays === "number" && validityDays > 0
      ? `In your hand for ${validityDays} ${
          validityDays === 1 ? "day" : "days"
        } — play before ${new Date(
          Date.now() + validityDays * 24 * 60 * 60 * 1000,
        ).toLocaleDateString([], { month: "short", day: "numeric" })}`
      : null;

  const content = (
    <AnimatePresence>
      {open ? (
        <motion.div
          className="card-reveal"
          role="dialog"
          aria-modal="true"
          aria-label={`Card revealed: ${card.name}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <button
            type="button"
            className="card-reveal__backdrop"
            aria-label="Close"
            onClick={onClose}
            tabIndex={-1}
          />

          <motion.div
            className={`card-reveal__panel card-reveal__panel--${rarity}`}
            initial={{ scale: 0.85, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", stiffness: 320, damping: 26 }}
          >
            <p className="card-reveal__rarity-pill">
              <span className={`card-reveal__rarity-pill-dot card-reveal__rarity-pill-dot--${rarity}`} />
              {RARITY_LABEL[rarity] ?? rarity}
            </p>

            <p className="card-reveal__headline">{revealText}</p>

            <div className="card-reveal__stage">
              {stage !== "revealed" ? <Sparkles rarity={rarity} /> : null}

              <div className={tcgFlipClass}>
                <div className="card-reveal__face card-reveal__face--back">
                  <div className="card-reveal__back-frame">
                    <span className="card-reveal__back-mark">n450s</span>
                    <span className="card-reveal__back-hint">Daily drop</span>
                  </div>
                </div>
                <div className="card-reveal__face card-reveal__face--front">
                  <RespectCard card={card} size="md" expandable={false} />
                </div>
              </div>
            </div>

            {revealed && handExpiryLine ? (
              <motion.p
                className="card-reveal__hand-expiry"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.25, delay: 0.05 }}
              >
                {handExpiryLine}
              </motion.p>
            ) : null}

            <motion.div
              className="card-reveal__actions"
              initial={{ opacity: 0 }}
              animate={{ opacity: revealed ? 1 : 0 }}
              transition={{ duration: 0.25, delay: revealed ? 0.1 : 0 }}
              style={{ pointerEvents: revealed ? "auto" : "none" }}
            >
              <Button
                variant="secondary"
                onClick={onClose}
                type="button"
                disabled={!revealed}
                aria-hidden={!revealed}
                tabIndex={revealed ? 0 : -1}
              >
                Add to my hand
              </Button>
            </motion.div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );

  return createPortal(content, document.body);
}
